import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Avatar,
  Stack,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { Theme } from "@emotion/react";
import NormalLeaveIcon from "../icons/NormalLeaveIcon";
import SpecialLeaveIcon from "../icons/SpecialLeaveIcon";
import ParentalLeaveIcon from "../icons/ParentalLeaveIcon";
import SavedLeaveIcon from "../icons/SavedLeaveIcon";
import RecurringLeaveIcon from "../icons/RecurringLeave";
import useAfasData from "../hooks/useAfasData";
import dayjs from "dayjs";
import { LanguageContext } from "../context/LanguageContext";

type LeaveType =
  | "V"
  | "recurring"
  | "saved"
  | "GV"
  | "BV"
  | "OUD-B"
  | "OUD-O"
  | "B2025"
  | "B2026"
  | "B2027"
  | "B2028";

interface LeaveRequest {
  id: string;
  name: string;
  type: LeaveType;
  typeDescription: string;
  start_date: string;
  end_date: string;
  hours: number;
}

const getIconByType = (type: LeaveType) => {
  switch (type) {
    case "V":
      return <NormalLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "B2025":
      return <RecurringLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "B2026":
      return <RecurringLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "B2027":
      return <RecurringLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "B2028":
      return <RecurringLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "saved":
      return <SavedLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "GV":
      return <ParentalLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "OUD-B":
      return <ParentalLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "OUD-O":
      return <ParentalLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    case "BV":
      return <SpecialLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
    default:
      return <NormalLeaveIcon sx={{ width: "20px", height: "20px", fontSize: "1rem" }} />;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAvatarColorByType = (type: LeaveType, theme: any) => {
  switch (type) {
    default:
      return theme.palette.green.lightest;
  }
};

const LeaveRequestComponent: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const rowsPerPage = 6;
  const theme: Theme = useTheme();
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<LeaveRequest[]>([]);
  const { data: latestLeave, isLoading: leaveIsLoading } = useAfasData("/leave/latest");
  useEffect(() => {
    if (!leaveIsLoading && latestLeave) {
      setItems(latestLeave);
    }
  }, [latestLeave]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Calculate pagination info
  const totalPages = Math.ceil(items.length / rowsPerPage) > 0 ? Math.ceil(items.length / rowsPerPage) : 1;
  const currentPageStart = page * rowsPerPage;
  const currentPageEnd = Math.min((page + 1) * rowsPerPage, items.length);
  const currentRequests = items.slice(currentPageStart, currentPageEnd);

  return (
    <>
      <TableContainer
        sx={{
          pt: 4,
        }}
      >
        <Table aria-label="leave requests table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pb: 2, fontWeight: "bold", paddingLeft: 0 }}>
                {strings.formatString(strings.leaverequests.latestrequests, items.length)}
              </TableCell>
              <TableCell sx={{ pb: 2, fontWeight: "bold" }}>{strings.leaverequests.date}</TableCell>
              <TableCell sx={{ pb: 2, fontWeight: "bold", paddingRight: 0 }}>
                {strings.leaverequests.hours}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaveIsLoading && (
              <Box mt={2} width={"64px"} height={"64px"}>
                <CircularProgress />
              </Box>
            )}
            {!leaveIsLoading &&
              currentRequests &&
              currentRequests.map((request) => {
                return (
                  <TableRow key={request.id}>
                    <TableCell sx={{ paddingLeft: 0 }}>
                      <Box display="flex" alignItems="center">
                        <Tooltip arrow title={request.typeDescription}>
                          <Avatar
                            sx={{
                              width: "30px",
                              height: "30px",
                              bgcolor: getAvatarColorByType(request.type, theme),
                              color: "#000",
                              mr: 2,
                            }}
                          >
                            {getIconByType(request.type)}
                          </Avatar>
                        </Tooltip>
                        <Box display="flex" alignItems="center">
                          {request.name}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {dayjs(request.start_date).format("D-MM-YYYY")} -{" "}
                      {dayjs(request.end_date).format("D-MM-YYYY")}
                    </TableCell>
                    <TableCell
                      sx={{
                        pr: 0,
                      }}
                    >
                      {request.hours} {strings.leaverequests.hour}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        sx={{
          mt: 3,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {totalPages !== 1 && (
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 0} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography component="span" sx={{ mx: 1 }}>
              {page + 1}/
              <Typography component={"span"} fontWeight={"bold"}>
                {totalPages}
              </Typography>
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= totalPages - 1}
              size="small"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        )}
      </Stack>
    </>
  );
};

export default LeaveRequestComponent;
